/**
 * Fair Home V2 — AI onboarding block (H7)
 * Figma RedisignHomePage `1:1353` desktop · `1:1462` compact
 */

export type FairHomeV2AiPersonaId =
  | 'guest'
  | 'registered-partial'
  | 'newcomer'
  | 'explorer'
  | 'advanced'
  | 'profile-complete'

/** Prop default only — the page passes the live persona (H10). */
export const FAIR_HOME_V2_AI_DEFAULT_PERSONA: FairHomeV2AiPersonaId =
  'registered-partial'

export type FairHomeV2AiCtaAction = 'auth' | 'profile' | 'taste'

export interface FairHomeV2AiPersonaView {
  completenessPercent: number
  progressFilledSegments: number
  progressTotalSegments: number
  levelNameKey: string
  levelStep: number
  levelTotal: number
  nextStepKey: string
  descriptionKey: string
  ctaKey: string
  ctaAction: FairHomeV2AiCtaAction
  showSignIn: boolean
  /** Own title instead of the shared «The more I know…» (guest, Figma `706:43280`). */
  titleKey?: string
  /** No profile yet — no completeness bar and no level line (T-006). */
  hideProgress?: boolean
  /** The server's level name (`profile-data.level.current_name`) — wins over `levelNameKey`. */
  levelName?: string
}

/**
 * The profile level the home block shows: `/v1/personalization/status.level` (BE-190), identical to
 * the hub's `profile-data.level`.
 */
export interface FairHomeV2AiProfileLevel {
  /** 0 novice … 3 expert */
  index: number
  name: string
  completenessPercent: number
}

const PERSONA_VIEWS: Record<FairHomeV2AiPersonaId, FairHomeV2AiPersonaView> = {
  guest: {
    completenessPercent: 0,
    progressFilledSegments: 0,
    progressTotalSegments: 2,
    levelNameKey: 'profile.levels.novice',
    levelStep: 0,
    levelTotal: 3,
    nextStepKey: 'fair_home_v2.ai.next_sign_in',
    descriptionKey: 'fair_home_v2.ai.description_guest',
    ctaKey: 'fair_home_v2.ai.cta_create_profile',
    ctaAction: 'auth',
    showSignIn: true,
    titleKey: 'fair_home_v2.ai.title_guest',
    hideProgress: true,
  },
  'registered-partial': {
    completenessPercent: 45,
    progressFilledSegments: 1,
    progressTotalSegments: 2,
    levelNameKey: 'profile.levels.explorer',
    levelStep: 1,
    levelTotal: 3,
    nextStepKey: 'fair_home_v2.ai.next_size_recommendations',
    descriptionKey: 'fair_home_v2.ai.description_partial',
    ctaKey: 'fair_home_v2.ai.cta_complete_profile',
    ctaAction: 'profile',
    showSignIn: false,
  },
  newcomer: {
    completenessPercent: 15,
    progressFilledSegments: 0,
    progressTotalSegments: 2,
    levelNameKey: 'profile.levels.novice',
    levelStep: 0,
    levelTotal: 3,
    nextStepKey: 'fair_home_v2.ai.next_style_quiz',
    descriptionKey: 'fair_home_v2.ai.description_newcomer',
    ctaKey: 'fair_home_v2.ai.cta_style_quiz',
    ctaAction: 'taste',
    showSignIn: false,
  },
  explorer: {
    completenessPercent: 45,
    progressFilledSegments: 1,
    progressTotalSegments: 2,
    levelNameKey: 'profile.levels.explorer',
    levelStep: 1,
    levelTotal: 3,
    nextStepKey: 'fair_home_v2.ai.next_size_recommendations',
    descriptionKey: 'fair_home_v2.ai.description_partial',
    ctaKey: 'fair_home_v2.ai.cta_complete_profile',
    ctaAction: 'profile',
    showSignIn: false,
  },
  advanced: {
    completenessPercent: 72,
    progressFilledSegments: 1,
    progressTotalSegments: 2,
    levelNameKey: 'profile.levels.advanced',
    levelStep: 2,
    levelTotal: 3,
    nextStepKey: 'fair_home_v2.ai.next_taste_refinement',
    descriptionKey: 'fair_home_v2.ai.description_advanced',
    ctaKey: 'fair_home_v2.ai.cta_complete_profile',
    ctaAction: 'profile',
    showSignIn: false,
  },
  'profile-complete': {
    completenessPercent: 100,
    progressFilledSegments: 2,
    progressTotalSegments: 2,
    levelNameKey: 'profile.levels.expert',
    levelStep: 3,
    levelTotal: 3,
    nextStepKey: 'fair_home_v2.ai.next_all_set',
    descriptionKey: 'fair_home_v2.ai.description_complete',
    ctaKey: 'fair_home_v2.ai.cta_explore_feed',
    ctaAction: 'profile',
    showSignIn: false,
  },
}

const AI_LEVEL_IDS = ['novice', 'explorer', 'advanced', 'expert'] as const

/** BE-190 — `/v1/personalization/status.level` → the block's level (`null` for a guest / no level). */
export function fairHomeV2AiLevelFromStatus(
  level: { current: string; completeness_percent: number } | null | undefined,
  nameOf: (current: string) => string,
): FairHomeV2AiProfileLevel | null {
  if (!level?.current) return null
  const index = (AI_LEVEL_IDS as readonly string[]).indexOf(level.current)
  return { index: index < 0 ? 0 : index, name: nameOf(level.current), completenessPercent: level.completeness_percent }
}

export function resolveFairHomeV2AiPersona(
  persona: FairHomeV2AiPersonaId = FAIR_HOME_V2_AI_DEFAULT_PERSONA,
  options?: { completenessPercent?: number; level?: FairHomeV2AiProfileLevel | null },
): FairHomeV2AiPersonaView {
  const base = PERSONA_VIEWS[persona]
  const level = persona === 'guest' ? null : options?.level ?? null
  const view: FairHomeV2AiPersonaView = level
    ? {
        ...base,
        levelName: level.name,
        levelStep: Math.max(0, Math.min(base.levelTotal, level.index)),
        completenessPercent: Math.max(0, Math.min(100, Math.round(level.completenessPercent))),
      }
    : base
  if (level || options?.completenessPercent == null) return view
  return {
    ...view,
    completenessPercent: Math.max(
      0,
      Math.min(100, Math.round(options.completenessPercent)),
    ),
  }
}

export function formatFairHomeV2AiCompleteness(value: number): string {
  return `${Math.max(0, Math.min(100, Math.round(value)))}%`
}
